// The picture a payer posts after a dinner: "7 friends. $412. All paid back in 2 hours." Drawn on the
// phone (story size, 1080 × 1920) from the split's real numbers, with the payer's own code printed on
// it, since a story picture can't carry a tappable link. Names and items never go on it.
import { unnamedVenue } from '../venue-name.ts';
import { brand, wordmarkSvg } from '../brand.ts';
import { fmt } from '../money.ts';

export interface CardFacts {
  venue: string;
  friends: number;
  paidCount: number;
  totalCents: number;
  /** Minutes from making the code to the last friend paying, once everyone has. */
  mins: number | null;
  /** The payer's own referral code, so anyone who sees the post can use it. */
  code?: string;
}

const W = 1080, H = 1920;

/** "The bill" is what a split is called when nobody typed the restaurant. */
const named = (f: CardFacts): boolean => !unnamedVenue(f.venue);

/** "12 minutes", "3 hours", "2 days". */
export function howLong(mins: number): string {
  if (mins < 59.5) { const m = Math.max(1, Math.round(mins)); return `${m} minute${m === 1 ? '' : 's'}`; }
  if (mins < 48 * 60) { const h = Math.round(mins / 60); return `${h} hour${h === 1 ? '' : 's'}`; }
  const d = Math.round(mins / 1440);
  return `${d} days`;
}

/** The three big lines, also used as the card's text for screen readers and the share message. */
export function cardLines(f: CardFacts): string[] {
  const people = `${f.friends} friend${f.friends === 1 ? '' : 's'}.`;
  const back = f.paidCount >= f.friends && f.mins !== null
    ? `All paid back in ${howLong(f.mins)}.`
    : `${f.paidCount} of ${f.friends} paid back.`;
  return [people, `${fmt(f.totalCents)}.`, back];
}

/** Wraps a line to the width, word by word. */
function wrap(ctx: CanvasRenderingContext2D, text: string, max: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > max && line) { out.push(line); line = word; } else line = next;
  }
  if (line) out.push(line);
  return out;
}

function loadSvg(svg: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('logo')); };
    img.src = url;
  });
}

export async function drawCard(f: CardFacts): Promise<Blob> {
  // Espresso ground on every phone, so the picture always looks the same.
  // Yellow is the total only. Red is the word Settled only. Never white on yellow.
  const c = {
    bg: brand.colors.light.hero, ink: brand.colors.light.onHero, muted: brand.colors.light.onHero2,
    highlight: brand.colors.light.highlight, onHighlight: brand.colors.light.onHighlight,
    settled: brand.colors.dark.settled, font: `${brand.fonts.display}, system-ui, sans-serif`,
  };
  try { await document.fonts.load(`800 120px ${c.font}`); } catch { /* the fallback font is fine */ }
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("This phone can't draw the picture.");
  ctx.fillStyle = c.bg;
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = 'alphabetic';

  // Dark wordmark: paper TAB, espresso YEAH on the highlighter.
  try {
    const logo = await loadSvg(wordmarkSvg('dark'));
    const logoH = 78;
    ctx.drawImage(logo, 96, 120, logoH * (logo.width / logo.height || 495 / 94), logoH);
  } catch { /* the lines below still tell the story */ }

  // Where, then the three big lines. The money line sits on the highlighter.
  ctx.font = `500 48px ${c.font}`;
  ctx.fillStyle = c.muted;
  if (named(f)) ctx.fillText(`At ${f.venue}`.slice(0, 40), 96, 520);
  const settled = f.paidCount >= f.friends && f.mins !== null;
  if (settled) {
    ctx.font = `800 72px ${c.font}`;
    ctx.fillStyle = c.settled;
    ctx.fillText('Settled.', 96, 640);
  }
  let y = settled ? 820 : 700;
  cardLines(f).forEach((text, i) => {
    const size = i === 2 ? 96 : 140;
    ctx.font = `800 ${size}px ${c.font}`;
    for (const line of wrap(ctx, text, W - 192)) {
      if (i === 1) {
        const w = ctx.measureText(line).width;
        ctx.fillStyle = c.highlight;
        ctx.fillRect(88, y - size * 0.82, w + 20, size * 0.98);
        ctx.fillStyle = c.onHighlight;
      } else ctx.fillStyle = c.ink;
      ctx.fillText(line, 96, y);
      y += i === 2 ? 116 : 160;
    }
    y += 16;
  });

  // The way in, bottom: the address and the payer's code.
  ctx.font = `600 52px ${c.font}`;
  ctx.fillStyle = c.ink;
  ctx.fillText(`Got the bill? ${brand.domain}`, 96, H - 260);
  if (f.code) {
    ctx.font = `500 44px ${c.font}`;
    ctx.fillStyle = c.muted;
    ctx.fillText(`Use my code ${f.code}`, 96, H - 180);
  }
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't make the picture."))), 'image/png'));
}

/**
 * Opens the phone's share sheet with the picture, a line of text and the payer's link (Instagram,
 * Messages, anything). Where sharing a picture isn't possible, saves it instead.
 */
export async function shareCard(f: CardFacts, link: string): Promise<'shared' | 'saved' | 'closed'> {
  const blob = await drawCard(f);
  const file = new File([blob], `${brand.name.replace(/\W+/g, '-')}-split.png`, { type: 'image/png' });
  const text = `${f.friends} friend${f.friends === 1 ? '' : 's'} paid me back${named(f) ? ` at ${f.venue}` : ''} with ${brand.name}. Got the bill? ${link}`;
  if (navigator.canShare?.({ files: [file] })) {
    try { await navigator.share({ files: [file], text }); return 'shared'; } catch { return 'closed'; }
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  return 'saved';
}
